import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';
import InvoiceDrawerContext, { InvoiceDrawerContextValue } from './InvoiceDrawerContext';
import { useAppDispatch } from 'src/app/hooks';
import { refreshProformaInvoice } from 'src/slices/proformaInvoicesSlice/proformaInvoicesSlice';
import { MessageType } from 'components/UI/snackbar/providers/SnackBarProvider';
import { ProformaInvoiceDraft } from 'src/types/proformaInvoice';
import ProformaInvoiceEntity from 'src/db/entities/proforma_invoice/ProformaInvoiceEntity';
import InvoiceDrawer from './InvoiceDrawer';

type DrawerState =
  | { mode: 'create'; data?: Partial<ProformaInvoiceDraft>; onSuccess?: () => void }
  // `data` is null while the invoice is being fetched.
  | { mode: 'update'; uuid: string; data: ProformaInvoiceEntity | null; onSuccess?: () => void }
  | { mode: 'closed' };

export default function InvoiceDrawerProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [drawerState, setDrawerState] = useState<DrawerState>({ mode: 'closed' });
  const openedUuidRef = useRef<string | null>(null);

  const close = () => {
    openedUuidRef.current = null;
    setDrawerState({ mode: 'closed' });
  };

  const openCreateForm: InvoiceDrawerContextValue['openCreateForm'] = ({
    defaultValues,
    onSuccess,
  } = {}) => {
    openedUuidRef.current = null;
    setDrawerState({ mode: 'create', data: defaultValues, onSuccess });
  };

  // Edits start from the invoice as stored on the server (including its settings),
  // not from the local copy, which may be older or miss fields.
  const openUpdateForm: InvoiceDrawerContextValue['openUpdateForm'] = async ({
    uuid,
    onSuccess,
  }) => {
    openedUuidRef.current = uuid;
    setDrawerState({ mode: 'update', uuid, data: null, onSuccess });

    try {
      const invoice = await dispatch(refreshProformaInvoice(uuid)).unwrap();
      if (openedUuidRef.current === uuid) {
        setDrawerState({ mode: 'update', uuid, data: invoice, onSuccess });
      }
    } catch (error) {
      if (openedUuidRef.current === uuid) {
        enqueueSnackbar({ message: t('message.server_error'), variant: MessageType.Error });
        close();
      }
    }
  };

  const mode = drawerState.mode === 'closed' ? null : drawerState.mode;

  return (
    <InvoiceDrawerContext.Provider
      value={{ isOpen: mode !== null, mode, openCreateForm, openUpdateForm, close }}
    >
      {children}
      <InvoiceDrawer
        mode={mode}
        createOptions={
          drawerState.mode === 'create'
            ? {
                defaultValues: drawerState.data,
                onSuccess: () => {
                  drawerState.onSuccess?.();
                  close();
                },
              }
            : undefined
        }
        updateOptions={
          drawerState.mode === 'update'
            ? {
                invoice: drawerState.data,
                onSuccess: () => {
                  drawerState.onSuccess?.();
                  close();
                },
              }
            : undefined
        }
        onClose={close}
      />
    </InvoiceDrawerContext.Provider>
  );
}
